import React, { useEffect, useRef, useState } from 'react';
import { LoadBIN, LoadJSON, getElementPositionOnCanvas, getImageSize, getRandomFloatInRange } from './Global.js';
import { Vector2, Vector3 } from './geometry.js';
import { ImageRecord } from './BackgroundCanvas.js';
import { motion } from "framer-motion";
import SkillTitle from './SkillTitle.js';

function shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
}

function Page1({ backgroundCanvasRef, aspectRatio, canvasResolution, LoadPage, currentPage }) {
    const [headerClass, setHeaderClass] = useState("off");
    const [arrowOpacity, setArrowOpacity] = useState(1);
    const [titleClass, setTitleClass] = useState("on");
    const [activeSkill, setActiveSkill] = useState(null);
    const currentIllustration = useRef("UN");

    // Create and shuffle the array of numbers as strings
    const factOrder = useRef(Array.from({ length: 49 }, (_, i) => (i + 1).toString()));
    shuffleArray(factOrder.current);
    const factIndex = useRef(0);

    const skillCategories = {
        "Front End": ["React.js", "JavaScript", "TypeScript", "HTML/CSS", "Tailwind CSS", "Three.js"],
        "Back End": ["Node.js", "Python", "PHP", "Flask", "Django", "C#", ".NET", "REST APIs", "Kubernetes"],
        "Databases": ["MySQL", "MongoDB", "PostgreSQL"],
        "Technologies": ["Git/GitHub", "Docker", "Figma", "Azure", "AWS/Cloud Services"],
        "Other": ["Linux", "UI/UX", "CI/CD", "Agile/Scrum", "Selenium", "AI/LLMs"]
    };
    
    

    useEffect(() =>{
        console.log("[Page1] prespawning level");
        currentIllustration.current = "none";
        backgroundCanvasRef.current.prespawnLevel(
            Math.floor(getRandomFloatInRange(0, 3.99)), 
            "frontIcon",
            Vector2.sizeFromText("50vw", "1vh"),
            Vector2.sizeFromText("49vw", "98vh"),
        );

        SpawnHTMLGravity();
    }, [backgroundCanvasRef, canvasResolution]);

    useEffect(() => {
        const handleScroll = () => {
            const header = document.querySelector(".header-holder");
            const title = document.getElementById("title");
            if (!header || !title) return;
    
            const headerRect = header.getBoundingClientRect();
            const titleRect = title.getBoundingClientRect();
    
            const headerCenterY = headerRect.top + headerRect.height / 2;
            const titleCenterY = titleRect.top + titleRect.height / 2;
    
            const isScrolledPast = titleCenterY < headerCenterY;
            
            // Only update state if the class actually changes
            if (isScrolledPast && headerClass !== "on") {
                setHeaderClass("on");
                setTitleClass("off");
            } else if (!isScrolledPast && headerClass !== "off") {
                setHeaderClass("off");
                setTitleClass("on");
            }
    
            // Adjust arrow opacity smoothly
            const scrollY = window.scrollY;
            const maxScroll = 100; // Adjust the value as needed
            setArrowOpacity(Math.max(1 - scrollY / maxScroll, 0));
        };
    
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Run once to set initial state
    
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [headerClass]);
    

    const illustrateFact = (tag, color) => {
        const defaultColor = new Vector3(190, 190, 190);
    
        // Hardcoded gradient of colors (example: RGB values)
        const gradient = [
            new Vector3(240, 83, 101), //old red
            new Vector3(92, 107, 209), // old blue
            new Vector3(239, 117, 102), //new red
        ];
    
        // Only select a new color if none is provided
        if (!color) {
            const useDefault = Math.random() < 0.1; // 10% chance
            if (useDefault) {
                color = defaultColor;
            } else {
                const randIndex = Math.floor(Math.random() * gradient.length);
                color = gradient[randIndex];
            }
        }
    
        tag = factOrder.current[factIndex.current];
        factIndex.current = (factIndex.current + 1) % factOrder.current.length;
    
        if (backgroundCanvasRef.current === null) {
            return;
        }
        currentIllustration.current = tag;
    
        LoadBIN(`json/lineart/${tag}.bin`, (data) => {
            let size = getImageSize(data);
            let mxHeight = Vector2.unit("80vh");
            let mxWidth = Vector2.unit("45vw");
    
            if (mxHeight <= mxWidth)
                size.scaleSize(null, mxHeight);
            else
                size.scaleSize(mxWidth, null);
            let pos = Vector2.sizeFromText("75w%", "50h%", backgroundCanvasRef.current);
    
            backgroundCanvasRef.current.drawPicture(
                data,
                pos,
                size,
                9999999,
                color,
                200,
                "frontIcon",
            );
        });
    };

    const applySkill = (event, skill) => {
        if (skill === null){
            backgroundCanvasRef.current.disbandImage("frontIcon");
        }
        setActiveSkill(skill);
    };

    const SpawnHTMLGravity = () => {
        const elementsWithCountered = document.querySelectorAll('[countered]');
        console.log(elementsWithCountered);
        elementsWithCountered.forEach(element => {
            if (backgroundCanvasRef.current) {
                backgroundCanvasRef.current.contourHTMLObject(element);
            }
        });
    };

    return (
        <div className="page1">
            <div className={`header-holder ${headerClass} on`}>
                <div className='header-left'>
                    <h1 className='h1-title'>
                        Joe Sharipov
                    </h1>
                    <h2 className="occupation-title"> Software Engineer</h2>
                </div>
                <div className='header-right'>
                    <a href="https://www.linkedin.com/in/jekuper/" target="_blank">
                        <div className="img-container">
                            <img src={`${process.env.PUBLIC_URL}/images/page1/linkedIn.png`}>
                            </img>
                        </div>
                    </a>
                    <a className='email-button' href="mailto:joesharipov@gmail.com" target="_blank">
                        <div className="img-container">
                            <img src={`${process.env.PUBLIC_URL}/images/page1/emailRound.png`}>
                            </img>
                        </div>
                    </a>
                    <a href="https://github.com/jekuper" target="_blank">
                        <div className="img-container">
                            <img src={`${process.env.PUBLIC_URL}/images/page1/github.png`}>
                            </img>
                        </div>
                    </a>
                    <a href="https://jekuper.itch.io/" target="_blank">
                        <div className="img-container">
                            <img src={`${process.env.PUBLIC_URL}/images/page1/itchio.png`}>
                            </img>
                        </div>
                    </a>
                </div>
            </div>
            <div id="title" className={`${titleClass}`}>
                <div className='title-wrapper' countered="1">
                    <h1 className='h1-title'>
                        Joe Sharipov
                    </h1>
                    <h2 className="occupation-title"> Software Engineer</h2>
                </div>
                <img style={{opacity: arrowOpacity}} id='title-arrow' src={`${process.env.PUBLIC_URL}/images/page1/ChevronBottom.png`}></img>
            </div>
            <div className='section'>
                <motion.h1 
                initial={{ y: 50, opacity: 0, scale:1 }}
                whileInView={{ y: 0, opacity: 1, scale:1 }}
                transition={{ duration: 0.5, ease: "easeInOut", delay: 0.5 }}
                viewport={{ once: true }}
                className="section-title">
                    Did you Know?
                </motion.h1>
                <div className='section-left'>
                    
                </div>
                <div className='section-right'>
                    <motion.p    
                    initial={{ x: -50, clipPath: "inset(0% 100% 0% 0%)" }}
                    whileInView={{ x: 0, clipPath: "inset(0% 0% 0% 0%)" }}
                    transition={{ duration: 0.5, ease: "easeInOut", delay: 0.5 }}
                    viewport={{ once: true }}>
                        Website is a self-written game engine. It has <span>physics</span>, <span>destruction system</span> and <span>cool animations</span>.<br></br><br></br> <span>Try clicking</span> on one of the skills below.
                    </motion.p>
                </div>
            </div>
            <div className='section skill-section'>
                <SkillTitle activeSkill={activeSkill}></SkillTitle>
                <div className={`skills ${activeSkill ? "" : "active"}`}>
                    <div className={`skill`} onClick={(e) => {illustrateFact("technologies"); applySkill(e, "Technologies");}}>
                        <div className='skill-name'>
                            Technologies
                            <div className='skill-underline' />  
                        </div>
                    </div>
                    <div className={`skill`} onClick={(e) => {illustrateFact("lineart/1"); applySkill(e, "Front End");}}>
                        <div className='skill-name'>
                            Front End 
                            <div className='skill-underline' />  
                        </div>
                    </div>
                    <div className={`skill`} onClick={(e) => {illustrateFact("lineart/10"); applySkill(e, "Back End");}}>
                        <div className='skill-name'>
                            Back End
                            <div className='skill-underline' />  
                        </div>
                    </div>
                    <div className={`skill`} onClick={(e) => {illustrateFact("other"); applySkill(e, "Other");}}>
                        <div className='skill-name'>
                            Other
                            <div className='skill-underline' />  
                        </div>
                    </div>
                </div>

                <div className={`skill-details ${activeSkill ? "active" : ""}`}>
                    <div className='background-stripe'>
                        <ul>
                            {activeSkill && skillCategories[activeSkill].map((skill, index) => (
                                <li key={index}>{skill}</li>
                            ))}
                        </ul>
                    </div>
                    <div className='background-square' />
                    <h1 className='skill-details-title'>{activeSkill}</h1>
                    <img className='skill-back-button' src={`${process.env.PUBLIC_URL}/images/page1/ChevronLeft.png`} onClick={(e) => {applySkill(e, null);}}></img>
                </div>
            </div>            
        </div>
    );
}

export default Page1;